import { Injectable } from '@angular/core';
import { HttpService } from '../../../shared/services/http/http.service';
import { UrlService } from '../../../shared/services/url/url.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, throwError } from 'rxjs';
import { ResponseType } from '../../../shared/services/http/http.service';

export interface RefusalReason {
  id: string;
  descricao: string;
}

export interface SubmitReceiptPayload {
  tradeId: string;
  chaveNota: string;
  imei: string;
  recusado: boolean;
  motivoRecusaId?: string | null;
  telaQuebrada: boolean;
  carcacaQuebrada: boolean;
  aparelhoNaoLiga: boolean;
  possuiTermoImpresso: boolean;
  loteId: string;
  retornoTradeId: string;
  retornoChaveNota: string;
  retornoNumeroNota: number | null;
  retornoIMEI: string;
  imagens?: { url: string }[];
}

export interface TradeReceiptNote {
  noteNumber: number;
  noteKey: string;
  imei: string;
  tradeId: string;
}

@Injectable({
  providedIn: 'root',
})
export class StockyEntryService {
  constructor(
    private _urlService: UrlService,
    private _httpService: HttpService,
    private toastr: ToastrService
  ) {}

  importBag(payload: { data: string; numeroNota: number | string; file: File }): Observable<any> {
    const formData = new FormData();

    formData.append('Arquivo', payload.file);
    formData.append('Data', payload.data);
    formData.append('Numero', payload.numeroNota.toString());

    const url = this._urlService.getUrl('API_URL_RECEIPT.importBag');

    return this._httpService
      .post(url, formData)
      .pipe(catchError((error: any) => throwError(error)));
  }

  getLots(params: { Filtro?: string; PageSize?: number; Page?: number } = {}): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.listLots');
    return this._httpService.get(url, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLotsClosed(
    params: { Filtro?: string; PageSize?: number; Page?: number } = {}
  ): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.listLotsclosed');
    return this._httpService.get(url, params).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLotsCombobox(): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.lotsCombobox');
    return this._httpService.get(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createLot(): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.newLot');
    return this._httpService.post(url, { status: 0 }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  searchTrade(search: string): Observable<TradeReceiptNote> {
    const encodedSearch = encodeURIComponent(search.trim());
    const url = this._urlService.getUrl('COLLECT_TVF.imeiDetail', {
      search: encodedSearch,
    });
    return this._httpService.get(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getRefusalReasons(): Observable<RefusalReason[]> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.refusalReasons');
    return this._httpService.get(url, {}).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  submitReceipt(payload: SubmitReceiptPayload): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.submitReceipt');
    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  uploadEvidence(tradeId: string, file: File): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.uploadEvidence');
    const formData = new FormData();
    formData.append('TradeId', tradeId);
    formData.append('Arquivo', file);
    return this._httpService.post(url, formData).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteEvidence(imageUrls: string[]): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.deleteEvidence');
    return this._httpService.deleteWithBody(url, { Urls: imageUrls }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getWaybillItems(lotId: string, params: { Page: number; PageSize: number }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.listWaybillItems');
    return this._httpService.get(url, { Id: lotId, ...params }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  printWaybill(lotId: string, sheetNumber: string): Observable<Blob> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.printWaybill', {
      lotId,
      numeroFolha: sheetNumber,
    });
    return this._httpService.get(url, {}, ResponseType.BLOB).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  downloadTermo(tradeId: string): Observable<Blob> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.downloadTermo', { tradeId });
    return this._httpService
      .get(url, {}, ResponseType.BLOB)
      .pipe(catchError((error) => throwError(() => error)));
  }

  closeLot(lotId: string, payload: { numeroFolha: number; dataEntrega: string }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.closeLot', { lotId });
    return this._httpService.put(url, { id: lotId, ...payload }).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  removeWaybillItem(itemId: string): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.removeWaybillItem', { itemId });
    return this._httpService.delete(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  batchDelivery(payload: { loteIds: string[]; loginResponsavel: string }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.batchDelivery');

    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLoteItems(filters: {
    LoteId?: string;
    TradeId?: string;
    PageSize?: number;
    Page?: number;
    CorrelationId?: string;
    Authenticated?: boolean;
    UserName?: string;
    UserId?: string;
  }): Observable<any> {
    const urlSource = 'API_URL_RECEIPT.submitReceipt';

    const url = this._urlService.getUrl(urlSource, {
      LoteId: filters?.LoteId,
      TradeId: filters?.TradeId,
      PageSize: filters?.PageSize,
      Page: filters?.Page,
      UserName: filters?.UserName,
      UserId: filters?.UserId,
    });

    return this._httpService.get(url, filters).pipe(catchError((error: any) => throwError(error)));
  }

  postLotItems(payload: {
    tradeId: string;
    chaveNota: string;
    imei: string;
    recusado: boolean;
    telaQuebrada: boolean;
    carcacaQuebrada: boolean;
    aparelhoNaoLiga: boolean;
    possuiTermoImpresso: boolean;
    loteId: string;
    retornoTradeId: string;
    retornoChaveNota: string;
    retornoIMEI: string;
  }): Observable<any> {
    const urlSource = 'PRODUCT_LOTS.lote';
    const url = this._urlService.getUrl(urlSource);

    return this._httpService.post(url, payload).pipe(catchError((error: any) => throwError(error)));
  }

  uploadLoteItem(tradeId: string, arquivo: File): Observable<any> {
    const urlSource = 'PRODUCT_LOTS.loteItemUpload';
    const url = this._urlService.getUrl(urlSource);

    const formData = new FormData();

    formData.append('TradeId', tradeId);
    formData.append('Arquivo', arquivo);

    return this._httpService
      .post(url, formData)
      .pipe(catchError((error: any) => throwError(error)));
  }

  receiveLots(payload: { loteIds: string[] }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.receiveLots');

    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  removeTrades(payload: { quantidade: number }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.removeTrades');

    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  returnLots(payload: {
    lotes: {
      loteId: string;
      motivo: string;
    }[];
    loginResponsavel: string;
  }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.returnLots');

    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
  getChargingResume(): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.resume');

    return this._httpService.get(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLotsWaiting(): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.waitingLots');

    return this._httpService.get(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getLotsLoading(): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.loadingLots');

    return this._httpService.get(url, {}).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  lotDevolution(payload: { lotes: any[] }): Observable<any> {
    const url = this._urlService.getUrl('API_URL_RECEIPT.returnLots');

    return this._httpService.post(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
